import psHttp from '@patsnap/common-ps-http';
import { EUREKA_SERVICE_URI, DRAFTING_URI } from '@/constants/base-url';

import type { IPackageType, DraftingPlan, EurekaAmountsControl } from '@/types/user';

/**
 * 获取用户套餐信息
 */
export async function getUserPackageTypeApi(options?: { refresh?: boolean }) {
    const response = await psHttp<{
        code: number;
        message: string;
        data: IPackageType[];
    }>({
        url: `${EUREKA_SERVICE_URI}/eureka/package-info`,
        method: 'GET',
        ...(options?.refresh ? { params: { refresh: true } } : {}),
        transformCode: true,
        headers: {
            'x-api-version': '1.0',
        },
    });
    return response.data.data || [];
}

/**
 * 获取 Eureka 权益菜单控制配置
 */
export async function getEurekaAmountsControlApi(): Promise<EurekaAmountsControl> {
    const response = await psHttp<EurekaAmountsControl | { data?: EurekaAmountsControl }>({
        url: `${EUREKA_SERVICE_URI}/eureka/amounts/control`,
        method: 'GET',
    });
    const responseData = response.data;
    if (responseData && 'data' in responseData && responseData.data) {
        return responseData.data;
    }
    return (responseData || {}) as EurekaAmountsControl;
}

/**
 * 检查当前用户是否为超级管理员
 */
export async function checkIsSuperAdminApi(): Promise<boolean> {
    try {
        const response = await psHttp<{
            status: boolean;
            message: string;
            data: {
                is_super_admin: boolean;
            };
        }>({
            url: `${EUREKA_SERVICE_URI}/user/is-super-admin`,
            method: 'GET',
            transformCode: true,
        });
        return response.data.data?.is_super_admin || false;
    } catch (error) {
        console.error('[checkIsSuperAdminApi] Failed to check super admin status:', error);
        return false;
    }
}

/**
 * 获取用户基础信息（包含 payment_customer_id）
 */
export async function getAccountBasicDataApi() {
    const { USER_SERVICE_URI } = await import('@/constants/base-url');
    const response = await psHttp<{
        status: boolean;
        error_code: number;
        data: {
            account_id: string;
            payment_customer_id?: string;
            email: string;
            [key: string]: unknown;
        };
    }>({
        url: `${USER_SERVICE_URI}/account-info/basic-data`,
        method: 'GET',
    });
    return response.data;
}

/**
 * 获取 Drafting 套餐计划
 */
export async function getDraftingPlanApi() {
    const response = await psHttp<{
        status: boolean;
        message: string;
        data: DraftingPlan;
    }>({
        url: `${DRAFTING_URI}/api/user/drafting-plan`,
        method: 'GET',
    });
    return response.data.data;
}

/**
 * 用户初始化接口
 */
export async function initUserApi() {
    try {
        const response = await psHttp<{
            status: boolean;
            message: string;
            data: unknown;
        }>({
            url: `${DRAFTING_URI}/api/user/init`,
            method: 'GET',
            transformCode: true,
        });
        return response.data;
    } catch (error) {
        console.error('[initUserApi] Failed to initialize user:', error);
        throw error;
    }
}

/**
 * 获取 AI 对话次数限制配置
 */
export async function getChatLimitApi() {
    const response = await psHttp<{
        status: boolean;
        message: string;
        data: {
            account_limit: number;
            visitor_limit: number;
        };
    }>({
        url: `${DRAFTING_URI}/api/user/settings/chat_limit`,
        method: 'GET',
        transformCode: true,
    });
    return response.data.data;
}

/**
 * 获取 Pro 用户文档数量限制（公开接口）
 */
export async function getProDocumentLimitsApi() {
    const response = await psHttp<{
        status: boolean;
        message: string;
        data: {
            pro_patent_draft_limit: number;
            pro_disclosure_limit: number;
            pro_oar_limit: number;
        };
    }>({
        url: `${DRAFTING_URI}/api/user/usage/pro_document_limits`,
        method: 'GET',
        transformCode: true,
    });
    return response.data.data;
}
